import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Folder, FolderPlus } from "lucide-react";


type ContentType = "risk_assessment" | "skr_assessment" | "work_preparation";

const TABLE_BY_TYPE: Record<ContentType, "folder_risk_assessments" | "skr_assessments" | "work_preparations"> = {
  risk_assessment: "folder_risk_assessments",
  skr_assessment: "skr_assessments",
  work_preparation: "work_preparations",
};

interface MoveToFolderDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  itemId: string;
  itemName: string;
  itemType: string;
  currentFolderId?: string | null;
  onMoved?: () => void;
}

export const MoveToFolderDialog: React.FC<MoveToFolderDialogProps> = ({
  isOpen,
  onOpenChange,
  projectId,
  itemId,
  itemName,
  itemType,
  currentFolderId,
  onMoved,
}) => {
  const [folders, setFolders] = useState<{ id: string; name: string }[]>([]);
  const [selected, setSelected] = useState<string | null>(currentFolderId ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  useEffect(() => {
    if (!isOpen || !projectId) return;
    const load = async () => {
      const { data, error } = await supabase
        .from("folders")
        .select("id, name")
        .eq("project_id", projectId)
        .order("name");
      if (!error && data) {
        setFolders(data);
        setIsCreating(data.length === 0);
      }
    };
    load();
    setSelected(currentFolderId ?? null);
    setNewFolderName("");
  }, [isOpen, projectId, currentFolderId]);

  const moveTo = async (folderId: string) => {
    const table = TABLE_BY_TYPE[(itemType as ContentType)] ?? "folder_risk_assessments";
    const { error } = await supabase
      .from(table)
      .update({ folder_id: folderId })
      .eq("id", itemId);
    if (error) throw error;
    toast.success(`"${itemName}" flyttades`);
    onOpenChange(false);
    onMoved?.();
  };

  const handleMove = async () => {
    if (!selected) {
      toast.error("Välj en mapp först");
      return;
    }
    setIsSaving(true);
    try {
      await moveTo(selected);
    } catch (e: any) {
      toast.error("Kunde inte flytta", { description: e?.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateAndMove = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setIsSaving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("folders")
        .insert({
          project_id: projectId,
          name,
          created_by: userData?.user?.id ?? null,
        })
        .select("id, name")
        .single();
      if (error) throw error;
      setFolders((prev) => [...prev, { id: data.id, name: data.name }]);
      setSelected(data.id);
      setIsCreating(false);
      setNewFolderName("");
      await moveTo(data.id);
    } catch (e: any) {
      toast.error("Kunde inte skapa mappen", { description: e?.message });
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Flytta till mapp</DialogTitle>
          <DialogDescription>
            Välj vilken mapp "{itemName}" ska ligga i.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {folders.length > 0 && (
            <div className="max-h-60 overflow-auto rounded-md border">
              {folders.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setSelected(f.id);
                    setIsCreating(false);
                  }}
                  className={`w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-accent ${
                    selected === f.id ? "bg-accent" : ""
                  }`}
                >
                  <Folder className="h-4 w-4 text-primary" />
                  <span className="font-medium">{f.name}</span>
                </button>
              ))}
            </div>
          )}

          {isCreating ? (
            <div className="rounded-md border p-3 space-y-2">
              <Label htmlFor="new-folder-name">Namn på ny mapp</Label>
              <Input
                id="new-folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="T.ex. Projektering"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateAndMove();
                  }
                }}
              />
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={handleCreateAndMove}
                  disabled={isSaving || !newFolderName.trim()}
                >
                  {isSaving ? "Skapar…" : "Skapa och flytta"}
                </Button>
                {folders.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsCreating(false)}
                    disabled={isSaving}
                  >
                    Avbryt
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setIsCreating(true);
                setSelected(null);
              }}
              className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-md border border-dashed hover:bg-accent text-sm font-medium"
            >
              <FolderPlus className="h-4 w-4 text-primary" />
              Skapa ny mapp
            </button>
          )}
        </div>


        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleMove} disabled={isSaving || !selected}>
            {isSaving ? "Flyttar…" : "Flytta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
